import { useCallback, useState } from "react"
import { useDispatch } from "react-redux"
import { processQuery, parseReceipt, fetchChatSessions, fetchChatHistory, deleteChatSession, submitPaymentMethod } from "store/aiassistant"
import { cappedToaster } from "components/common/Toast"

function newSessionId() {
  return `s_${Math.floor(Math.random() * 100000)}`
}

function mapSessions(raw) {
  return raw.map((s) => ({
    sessionId: s.session_id,
    title: s.title || "New conversation",
    timestamp: s.last_activity_at ? new Date(s.last_activity_at).getTime() : 0
  }))
}

function mapHistoryExchanges(raw) {
  return raw.flatMap((exchange, i) => [
    { id: `h-${i}-u`, role: "user", content: exchange.user },
    { id: `h-${i}-a`, role: "ai", content: exchange.assistant, options: exchange.options || [] }
  ])
}

export function useChatSession() {
  const dispatch = useDispatch()
  const [sessionId, setSessionId] = useState(null)
  const [chatHistory, setChatHistory] = useState([])
  const [sessions, setSessions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [deletingSessionId, setDeletingSessionId] = useState(null)

  const startNewSession = useCallback(() => {
    setSessionId(newSessionId())
    setChatHistory([])
  }, [])

  const sendMessage = useCallback(
    async (query, receiptFile) => {
      const filePreview = receiptFile ? URL.createObjectURL(receiptFile) : null
      setChatHistory((prev) => [...prev, { id: crypto.randomUUID(), role: "user", content: query, filePreview }])
      setIsLoading(true)
      try {
        let receiptMetadata = null
        if (receiptFile) {
          const parsed = await dispatch(parseReceipt(receiptFile))
          receiptMetadata = parsed?.data ?? null
        }
        const data = await dispatch(processQuery(sessionId, query, receiptMetadata))
        if (data.session_id) setSessionId(data.session_id)
        setChatHistory((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "ai",
            content: data.details || "",
            options: data.options || [],
            fileUpload: !!data.file_upload,
            addCard: data.add_card || null
          }
        ])
      } catch {
        setChatHistory((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: "ai", content: "Sorry, there was an error. Please try again.", options: [] }
        ])
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, sessionId]
  )

  const submitCard = useCallback(
    async (addCard, paymentMethodId) => {
      setIsLoading(true)
      try {
        // Attach the card first; this throws (skips to catch) on any non-2xx response,
        // so the "card added" follow-up only fires once the payment-method API succeeds.
        const cardResult = await dispatch(submitPaymentMethod(addCard, paymentMethodId))
        if (cardResult?.session_id) setSessionId(cardResult.session_id)

        const data = await dispatch(processQuery(cardResult?.session_id || addCard?.session_id || sessionId, "card added"))
        if (data.session_id) setSessionId(data.session_id)
        setChatHistory((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "ai",
            content: data.details || "",
            options: data.options || [],
            fileUpload: !!data.file_upload,
            addCard: data.add_card || null
          }
        ])
      } catch {
        setChatHistory((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: "ai", content: "Sorry, there was an error processing your card. Please try again.", options: [] }
        ])
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, sessionId]
  )

  const loadSessions = useCallback(async () => {
    setIsLoadingHistory(true)
    try {
      const data = await dispatch(fetchChatSessions())
      const list = Array.isArray(data) ? data : data?.sessions ?? []
      setSessions(mapSessions(list))
    } catch {
      setSessions([])
    } finally {
      setIsLoadingHistory(false)
    }
  }, [dispatch])

  const loadSession = useCallback(
    async (session) => {
      setSessionId(session.sessionId)
      setChatHistory([])
      setIsLoading(true)
      try {
        const data = await dispatch(fetchChatHistory(session.sessionId))
        if (Array.isArray(data)) setChatHistory(mapHistoryExchanges(data))
      } catch {
        setChatHistory([])
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch]
  )

  const markOptionSelected = useCallback((msgId, selectedOption) => {
    setChatHistory((prev) => prev.map((m) => m.id === msgId ? { ...m, optionSelected: selectedOption } : m))
  }, [])

  const deleteSession = useCallback(
    async (sid, currentSessionId, onNewSession) => {
      setDeletingSessionId(sid)
      try {
        await dispatch(deleteChatSession(sid))
        setSessions((prev) => prev.filter((s) => s.sessionId !== sid))
        if (sid === currentSessionId) onNewSession()
        cappedToaster("delete-chat-session-success", "success", "Conversation deleted")
      } catch {
        cappedToaster("delete-chat-session-error", "error", "Failed to delete conversation")
      } finally {
        setDeletingSessionId(null)
      }
    },
    [dispatch]
  )

  return {
    sessionId,
    chatHistory,
    sessions,
    isLoading,
    isLoadingHistory,
    deletingSessionId,
    startNewSession,
    sendMessage,
    submitCard,
    loadSessions,
    loadSession,
    deleteSession,
    markOptionSelected
  }
}
